import type { ThemeMode } from './tokens';

/**
 * Where a chosen theme mode is remembered.
 *
 * **Two states, not three.** The key holds `light` or `dark`, and its *absence*
 * is what means "follow the operating system". A stored `system` literal would
 * be a third value meaning the same thing as no value at all.
 */
const STORAGE_KEY = 'augment-toolkit.theme-mode';

/** The query that answers "is the OS asking for dark". */
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const isMode = (value: string | null): value is ThemeMode =>
  value === 'light' || value === 'dark';

/**
 * The remembered mode, or `null` when there is none.
 *
 * **Storage is a boundary and it throws.** Safari in private browsing and a
 * browser with site data blocked both raise on access, so an unreadable store
 * reads as "nothing chosen" rather than taking the app down.
 */
export const readStoredMode = (): ThemeMode | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    return isMode(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Remembers a choice, or silently does not where storage refuses. */
export const storeMode = (mode: ThemeMode): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // The toggle still works for this visit; it just will not be remembered.
  }
};
